import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBars, 
  faGlobe, 
  faWarehouse, 
  faUser, 
  faRightFromBracket,
  faKey
} from "@fortawesome/free-solid-svg-icons";
import {
  Navbar,
  Button,
  NavbarToggler,
  Collapse,
  Nav,
  Dropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import AuthService from "../Services/AuthService";
import UsersService from "../Services/UsersService";
import { useToasts } from 'react-toast-notifications';
import { withTranslation, setLanguage, getLanguage } from 'react-multi-lang';

const Topbar = ({ toggleSidebar, t }) => {
  const [topbarIsOpen, setTopbarOpen] = useState(true);
  const toggleTopbar = () => setTopbarOpen(!topbarIsOpen);
  const [chooseWhMenuOpen, setChooseWhMenuOpen] = useState(false);
  const [userMenuOpen, setuserMenuOpen] = useState(false);
  const [config, setConfig] = useState(false);
  const [user, setUser] = useState(false);
  const [availableConfigs, setAvailableConfigs] = useState([]);
  const [chooseLangMenuOpen, setChooseLangMenuOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ newPassword: '', confirmPassword: '' });
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);

  const { addToast } = useToasts();
  const authService = new AuthService();
  const usersService = new UsersService();

  useEffect(() => {
    let con = authService.getConfig();
    setConfig(con);
    setAvailableConfigs(authService.getAvailableConfigs());
    let u = authService.getUser();
    setUser(u);
  }, []);

  const getLangByCode = (code) => {
    if(code === 'gr') return "ΕΛ";
    if(code === 'en') return 'EN';
    if(code === 'mc') return 'MKD';
    return code?.toUpperCase() || 'ΕΛ';
  };

  const updateConfig = (conf) => {
    authService.setConfig(conf);
    setConfig(conf);
    window.dispatchEvent(new CustomEvent('warehouseChanged', { detail: conf }));
    window.dispatchEvent(new Event('storage'));
  };

  const dologout = () => {
    authService.logout();
    addToast("Αποσυνδεθήκατε από το σύστημα. Αναμένετε ανακατεύθυνση", { appearance: 'warning', autoDismiss: true, autoDismissTimeout: 5000 });
  };

  const togglePasswordModal = () => {
    if (passwordModalOpen) {
      setPasswordData({ newPassword: '', confirmPassword: '' });
    }
    setPasswordModalOpen(!passwordModalOpen);
  };

  const openPasswordModal = () => {
    setPasswordData({ newPassword: '', confirmPassword: '' });
    setuserMenuOpen(false);
    setPasswordModalOpen(true);
  };

  const isValidPassword = (password) => {
    if (!password || password.length < 8) return false;
    return /[a-z]/.test(password) && /[A-Z]/.test(password) && /[0-9]/.test(password);
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    if (!passwordData.newPassword) {
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      addToast(t('users.passwordsDontMatch') || 'Passwords do not match', { appearance: 'error', autoDismiss: true });
      return;
    }
    if (!isValidPassword(passwordData.newPassword)) {
      addToast(t('users.passwordComplexityError') || 'Ο κωδικός πρέπει να αποτελείται από τουλάχιστον 8 χαρακτήρες και να περιέχει τουλάχιστον ένα πεζό, ένα κεφαλαίο γράμμα και έναν αριθμό', { appearance: 'error', autoDismiss: true });
      return;
    }

    const currentUser = authService.getUser();
    if (!currentUser || !currentUser.userId) {
      addToast('User session error', { appearance: 'error', autoDismiss: true });
      return;
    }

    setIsSubmittingPassword(true);
    usersService.changePassword(currentUser.userId, passwordData.newPassword).then(() => {
      setIsSubmittingPassword(false);
      addToast(t('users.changePasswordSuccess') || 'Password changed successfully', { appearance: 'success', autoDismiss: true });
      setPasswordData({ newPassword: '', confirmPassword: '' });
      setPasswordModalOpen(false);
    }).catch(err => {
      setIsSubmittingPassword(false);
      addToast(err || t('users.saveError') || 'Error changing password', { appearance: 'error', autoDismiss: true });
    });
  };

  return (
    <>
      <Navbar
        dark
        className="topbar-navbar"
        expand="md"
      >
        <button className="sidebar-toggle-btn" onClick={toggleSidebar} title="Toggle Navigation">
          <FontAwesomeIcon icon={faBars} />
        </button>

        <NavbarToggler onClick={toggleTopbar} />

        <Collapse isOpen={topbarIsOpen} navbar>
          <Nav className="ms-auto d-flex align-items-center flex-wrap gap-2" navbar>
            {/* Language Selector */}
            <Dropdown isOpen={chooseLangMenuOpen} toggle={() => setChooseLangMenuOpen(!chooseLangMenuOpen)}>
              <DropdownToggle className="topbar-pill-btn">
                <FontAwesomeIcon icon={faGlobe} className="text-muted small" />
                <span>{getLangByCode(getLanguage())}</span>
              </DropdownToggle>
              <DropdownMenu end>
                <DropdownItem onClick={() => setLanguage('gr')}>
                  🇬🇷 Ελληνικά (ΕΛ)
                </DropdownItem>
                <DropdownItem onClick={() => setLanguage('en')}>
                  🇬🇧 English (EN)
                </DropdownItem>
                <DropdownItem onClick={() => setLanguage('mc')}>
                  🇲🇰 Македонски (MKD)
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>

            {/* Warehouse Selector */}
            <Dropdown isOpen={chooseWhMenuOpen} toggle={() => setChooseWhMenuOpen(!chooseWhMenuOpen)}>
              <DropdownToggle className="topbar-pill-btn">
                <FontAwesomeIcon icon={faWarehouse} className="text-muted small" />
                <span>{config ? config.name : t('topBar.selectWh')}</span>
              </DropdownToggle>
              <DropdownMenu end>
                {availableConfigs.map(ac => (
                  <DropdownItem key={ac.whCode} onClick={() => updateConfig(ac)}>
                    {ac.name}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>

            {/* User Profile */}
            <Dropdown isOpen={userMenuOpen} toggle={() => setuserMenuOpen(!userMenuOpen)}>
              <DropdownToggle className="topbar-user-btn">
                <FontAwesomeIcon icon={faUser} />
                <span>{user?.name || 'User'}</span>
              </DropdownToggle>
              <DropdownMenu end>
                <DropdownItem header className="text-muted small">
                  {t('topBar.role') || 'Ρόλος'}: <strong>{user?.role || 'N/A'}</strong>
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem onClick={openPasswordModal} className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faKey} />
                  <span>{t('topBar.changePassword')}</span>
                </DropdownItem>
                <DropdownItem onClick={dologout} className="text-danger d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faRightFromBracket} />
                  <span>{t('topBar.logout')}</span>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </Nav>
        </Collapse>
      </Navbar>

      {/* Change Password Modal */}
      <Modal isOpen={passwordModalOpen} toggle={togglePasswordModal} centered>
        <Form onSubmit={handleChangePassword}>
          <ModalHeader toggle={togglePasswordModal}>
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faKey} className="text-primary" />
              <span>{t('topBar.changePassword')}</span>
            </div>
          </ModalHeader>
          <ModalBody>
            <FormGroup className="mb-3">
              <Label className="fw-medium">{t('users.newPassword')}</Label>
              <Input 
                type="password" 
                required
                value={passwordData.newPassword} 
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} 
                placeholder={t('users.newPassword')}
              />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="fw-medium">{t('users.confirmPassword')}</Label>
              <Input 
                type="password" 
                required
                value={passwordData.confirmPassword} 
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} 
                placeholder={t('users.confirmPassword')}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" type="submit" disabled={isSubmittingPassword}>
              {isSubmittingPassword ? '...' : (t('volumes.save') || 'Save')}
            </Button>
            <Button color="secondary" onClick={togglePasswordModal} type="button">
              {t('volumes.cancel') || 'Cancel'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
};

export default withTranslation(Topbar);
